import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { translations } from '../../../translations';
import { describeApiError, formatDateTime } from '../../../utils/format';
import { requestJson } from '../../../utils/lifecycle';

function StatusControl({ app, onChanged }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ kind: '', text: '' });
  const moves = app.allowedTransitions || [];

  const submit = async (event) => {
    event.preventDefault();
    if (!target) return;
    setBusy(true);
    setMessage({ kind: '', text: '' });
    try {
      await requestJson(`/custom-apps/${app.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: target }),
      });
      setTarget('');
      setMessage({ kind: 'ok', text: t.statusUpdated });
      await onChanged();
    } catch (error) {
      if (error.status !== 401) setMessage({ kind: 'error', text: describeApiError(error, t, error.message) });
    } finally {
      setBusy(false);
    }
  };

  return <form className="tile stack" onSubmit={submit}>
    <h3>{t.statusSection}</h3>
    {moves.length === 0
      ? <p className="hint">{t.noMoves}</p>
      : <div className="form-grid">
        <div className="field">
          <label className="label" htmlFor="status-target">{t.moveTo}</label>
          <select id="status-target" className="select" value={target} onChange={(event) => setTarget(event.target.value)}>
            <option value="">{t.chooseStatus}</option>
            {moves.map((status) => <option key={status} value={status}>{t.statusLabel[status] || status}</option>)}
          </select>
        </div>
        <div className="field"><span className="label" aria-hidden="true">&nbsp;</span><button className="btn btn--primary" type="submit" disabled={!target || busy}>{t.updateStatus}</button></div>
      </div>}
    <p className={message.kind === 'error' ? 'notice notice--error' : 'hint'} role={message.kind === 'error' ? 'alert' : 'status'}>{message.text}</p>
  </form>;
}

export default function OverviewTab({ app, onChanged }) {
  const { language } = useLanguage();
  const t = translations[language];
  const company = app.company || app.companySnapshot || {};
  const colors = app.colors || {};
  const swatch = (value) => (value
    ? <span className="swatch"><i style={{ background: value }} aria-hidden="true" />{value}</span>
    : t.notSet);

  return <section className="card stack stack--lg">
    <div className="split split--even">
    <div className="stack">
      <div>
        <p className="page-head__eyebrow">{t.company}</p>
        <h2>{company.name || t.unmappedCompany}</h2>
        <p className="hint">{t.companyId}: {company.id || t.notSet}</p>
      </div>
      <div>
        <h3>{t.notes}</h3>
        <p>{app.notes || t.noNotes}</p>
      </div>
    </div>
    <dl className="facts">
      <div><dt>{t.packageName}</dt><dd>{app.packageName || app.appInfo?.packageName || t.notSet}</dd></div>
      <div><dt>{t.banks}</dt><dd>{(app.banks || []).join(', ') || t.notSet}</dd></div>
      <div><dt>{t.primaryColorLabel}</dt><dd>{swatch(colors.primary)}</dd></div>
      <div><dt>{t.secondaryColorLabel}</dt><dd>{swatch(colors.secondary)}</dd></div>
      <div><dt>{t.requestedBy}</dt><dd>{app.createdBy?.name || t.notSet}{app.createdBy?.role ? ` (${app.createdBy.role})` : ''}</dd></div>
      <div><dt>{t.createdAt}</dt><dd>{formatDateTime(app.createdAt, language)}</dd></div>
      <div><dt>{t.updatedAt}</dt><dd>{formatDateTime(app.updatedAt, language)}</dd></div>
    </dl>
    </div>
    <StatusControl app={app} onChanged={onChanged} />
  </section>;
}
